import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { typography } from '../styles/designSystem';
import { useCalPopup } from '../utils/useCalPopup';

// A slow brightness lift, so the chip reads as a live indicator rather than a label.
// Brightness rather than opacity: the chip is a solid fill, and pulsing its opacity
// would visibly flicker the text sitting on it.
const pulse = keyframes`
    0%, 100% { filter: brightness(1); }
    50%      { filter: brightness(1.22); }
`;

const BannerWrapper = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.background};
    border-bottom: 1px solid ${({ theme }) => theme.borderMedium};
    color: ${({ theme }) => theme.primary};
    padding: 0.55rem 0.75rem;
    overflow: hidden;
    font-family: ${typography.fontFamily.display};
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    text-align: center;
    text-shadow: 0 0 8px ${({ theme }) => theme.glow};

    @media (max-width: 768px) {
        font-size: 0.62rem;
        letter-spacing: 0.1em;
        padding: 0.4rem 0.5rem;
    }
`;

// Rendered in the same inverted state the terminal buttons take on hover: solid
// phosphor fill, screen-colored text, and a glow. It is the one thing on the banner
// that should catch the eye — and it opens the booking popup, so the affordance pays off.
const Status = styled.button`
    display: inline-block;
    border: none;
    cursor: pointer;
    /* Inherit the banner's display face, size, tracking, and casing. */
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    margin: 0 0.2rem;
    /* The wrapper's letter-spacing trails after the final character, so trim that
       phantom space back off the right padding to keep the fill optically even. */
    padding: 0.2rem calc(0.7rem - ${typography.letterSpacing.wider}) 0.2rem 0.7rem;
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.background};
    text-shadow: none;
    box-shadow: 0 0 14px ${({ theme }) => theme.glow};
    ${css`animation: ${pulse} 2.4s ease-in-out infinite;`}

    /* Drop the pulse on hover so the animated filter stops fighting the static one,
       and escalate past the resting state the chip already sits in. */
    &:hover,
    &:focus-visible {
        animation: none;
        filter: brightness(1.3);
        box-shadow: 0 0 24px ${({ theme }) => theme.glow};
    }

    &:active {
        filter: brightness(0.92);
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Supporting context on either side of the status. Both drop away on narrower
// screens so the line never wraps — the status itself always survives.
const Dim = styled.span`
    color: ${({ theme }) => theme.subtleText};
    text-shadow: none;

    @media (max-width: ${({ $until }) => $until}) {
        display: none;
    }
`;

const OpenToWorkBanner = () => {
    const calProps = useCalPopup("30min");

    return (
        <BannerWrapper>
            <Dim $until="640px">Vault-Tec Occupational Status &#47;&#47; </Dim>
            <Status
                {...calProps}
                type="button"
                aria-label="Available for hire — book a call"
            >
                Available for hire
            </Status>
            <Dim $until="900px"> &#47;&#47; Senior Software Engineer &middot; AI &amp; Mobile</Dim>
        </BannerWrapper>
    );
};

export default OpenToWorkBanner;
